import { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckSquare, FileText, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { LoadError } from '../../../components/layout/LoadError';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useToast } from '../../../components/ui/toast';
import { useCreateDocProfileRevision, useDocKitCatalog, useDocKitProfile } from '../../../hooks/doc-kit';
import { ApiError } from '../../../lib/api-client';
import type { AckMode, CatalogTemplate, DocKitResponse } from '@payroll/shared';

const ACK_LABEL: Record<AckMode, string> = {
  none: 'ไม่ต้องรับทราบ',
  button: 'กดรับทราบ',
  signature: 'ลงนาม',
};

const ACK_STORAGE_KEY = 'paypilot.doc-kit.ack-overrides';

function isUnavailable<T extends object>(data: DocKitResponse<T> | undefined): data is { unavailable: string } {
  return Boolean(data && 'unavailable' in data);
}

function actionError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function readAckOverrides(): Record<string, AckMode> {
  try {
    const stored = sessionStorage.getItem(ACK_STORAGE_KEY);
    return stored ? JSON.parse(stored) as Record<string, AckMode> : {};
  } catch {
    return {};
  }
}

function writeAckOverrides(overrides: Record<string, AckMode>): void {
  try {
    sessionStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Private browsing may reject session storage; selection itself still persists on the profile.
  }
}

function SelectSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-label="กำลังโหลดรายการเอกสาร">
      {[0, 1, 2].map((index) => <div key={index} className="card h-36 bg-gray-50" />)}
    </div>
  );
}

function TemplateCard({
  template,
  checked,
  ackMode,
  onChecked,
  onAckMode,
}: {
  template: CatalogTemplate;
  checked: boolean;
  ackMode: AckMode;
  onChecked: (checked: boolean) => void;
  onAckMode: (mode: AckMode) => void;
}) {
  return (
    <article className={`card p-4 ${checked ? 'border-brand-300 ring-1 ring-brand-100' : ''}`}>
      <div className="flex items-start gap-3">
        <label className="flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-center" aria-label={`เลือก ${template.title}`}>
          <input
            type="checkbox"
            className="h-5 w-5 accent-brand-600"
            checked={checked}
            disabled={template.is_core}
            onChange={(event) => onChecked(event.target.checked)}
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold break-words">{template.title}</h2>
            {template.is_core && <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">จำเป็น</span>}
            {template.recommended && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">แนะนำ</span>}
          </div>
          <p className="mt-1 text-sm text-gray-600">รหัสเอกสาร {template.code} · ฉบับเทมเพลต {template.version}</p>
          <div className="mt-3 max-w-sm">
            <label className="label" htmlFor={`ack-${template.code}`}>การรับทราบ</label>
            <select
              id={`ack-${template.code}`}
              className="input min-h-11 text-base"
              value={ackMode}
              disabled={!checked}
              onChange={(event) => onAckMode(event.target.value as AckMode)}
            >
              {(Object.keys(ACK_LABEL) as AckMode[]).map((mode) => <option key={mode} value={mode}>{ACK_LABEL[mode]}</option>)}
            </select>
          </div>
        </div>
      </div>
      {template.is_core && <p className="mt-3 text-xs text-gray-500">เอกสารนี้เป็นเอกสารหลักขององค์กร จึงเลือกไว้เสมอ</p>}
    </article>
  );
}

export function DocKitSelect() {
  const navigate = useNavigate();
  const toast = useToast();
  const catalogQuery = useDocKitCatalog();
  const profileQuery = useDocKitProfile();
  const saveRevision = useCreateDocProfileRevision();
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [ackOverrides, setAckOverrides] = useState<Record<string, AckMode>>(readAckOverrides);
  const initializedFor = useRef<string | null>(null);

  const catalog = !isUnavailable(catalogQuery.data) ? catalogQuery.data : undefined;
  const profileData = !isUnavailable(profileQuery.data) ? profileQuery.data : undefined;

  useEffect(() => {
    if (!catalog || !profileData) return;
    const marker = profileData.profile ? String(profileData.profile.revision) : 'new';
    if (initializedFor.current === marker) return;
    const saved = profileData.profile?.selected_codes ?? [];
    const defaults = catalog.templates.filter((template) => template.is_core || template.recommended).map((template) => template.code);
    const coreCodes = catalog.templates.filter((template) => template.is_core).map((template) => template.code);
    setSelectedCodes([...new Set([...(saved.length > 0 ? saved : defaults), ...coreCodes])]);
    setAckOverrides(readAckOverrides());
    initializedFor.current = marker;
  }, [catalog, profileData]);

  function setSelected(template: CatalogTemplate, checked: boolean) {
    setSelectedCodes((current) => {
      if (template.is_core || (checked && current.includes(template.code))) return current;
      if (checked) return [...current, template.code];
      return current.filter((code) => code !== template.code);
    });
  }

  function setAckMode(code: string, mode: AckMode) {
    setAckOverrides((current) => {
      const next = { ...current, [code]: mode };
      writeAckOverrides(next);
      return next;
    });
  }

  async function save(next: 'stay' | 'continue') {
    if (!profileData) return;
    if (next === 'continue' && selectedCodes.length === 0) {
      toast.error('เลือกเอกสารอย่างน้อยหนึ่งฉบับก่อนทำขั้นตอนต่อ');
      return;
    }
    try {
      const response = await saveRevision.mutateAsync({
        expected_revision: profileData.profile?.revision ?? null,
        answers: profileData.profile?.answers ?? {},
        selected_codes: selectedCodes,
        wizard_step: profileData.profile?.wizard_step ?? 1,
      });
      if (isUnavailable(response)) {
        toast.error(response.unavailable);
        return;
      }
      toast.success('บันทึกรายการเอกสารแล้ว');
      if (next === 'continue') navigate(`/hr/doc-kit/wizard/${Math.min(6, Math.max(1, response.profile.wizard_step))}`);
    } catch (error) {
      toast.error(actionError(error, 'บันทึกรายการเอกสารไม่สำเร็จ กรุณาลองใหม่'));
    }
  }

  if (catalogQuery.isLoading || profileQuery.isLoading) {
    return <AdminLayout title="เลือกเอกสารบริษัท"><SelectSkeleton /></AdminLayout>;
  }
  if (catalogQuery.error || profileQuery.error || !catalogQuery.data || !profileQuery.data) {
    return (
      <AdminLayout title="เลือกเอกสารบริษัท">
        <LoadError error={catalogQuery.error ?? profileQuery.error} onRetry={() => { void catalogQuery.refetch(); void profileQuery.refetch(); }} />
      </AdminLayout>
    );
  }
  const unavailableMessage = isUnavailable(catalogQuery.data)
    ? catalogQuery.data.unavailable
    : isUnavailable(profileQuery.data)
      ? profileQuery.data.unavailable
      : null;
  if (unavailableMessage) {
    return <AdminLayout title="เลือกเอกสารบริษัท"><div className="card border-l-4 border-l-amber-400 p-4 text-sm">{unavailableMessage}</div></AdminLayout>;
  }
  if (!catalog || !profileData) {
    return <AdminLayout title="เลือกเอกสารบริษัท"><LoadError onRetry={() => { void catalogQuery.refetch(); void profileQuery.refetch(); }} /></AdminLayout>;
  }
  if (catalog.templates.length === 0) {
    return (
      <AdminLayout title="เลือกเอกสารบริษัท">
        <EmptyState icon={FileText} title="ยังไม่มีเทมเพลตเอกสาร" detail="ติดต่อผู้ดูแลระบบเพื่อเตรียมเทมเพลตเอกสารก่อนเริ่มต้น" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="เลือกเอกสารบริษัท" subtitle="เลือกเอกสารที่องค์กรต้องจัดทำ และกำหนดวิธีรับทราบของแต่ละฉบับ">
      <div className="max-w-4xl space-y-4 pb-24">
        <section className="card p-4 text-sm text-gray-600">
          <div className="flex items-start gap-3"><CheckSquare className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" /><p>เอกสารที่ระบุว่า “จำเป็น” เลือกไว้เสมอ ส่วนเอกสาร “แนะนำ” ถูกเลือกไว้ล่วงหน้าตามข้อมูลขององค์กร และปรับได้ก่อนบันทึก</p></div>
        </section>
        <div className="space-y-3">
          {catalog.templates.map((template) => (
            <TemplateCard
              key={template.code}
              template={template}
              checked={selectedCodes.includes(template.code)}
              ackMode={ackOverrides[template.code] ?? template.default_ack}
              onChecked={(checked) => setSelected(template, checked)}
              onAckMode={(mode) => setAckMode(template.code, mode)}
            />
          ))}
        </div>
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white/95 p-3 backdrop-blur md:static md:border-0 md:bg-transparent md:p-0">
          <div className="mx-auto flex max-w-4xl flex-col gap-2 sm:flex-row sm:justify-end">
            <button className="btn-secondary min-h-11" disabled={saveRevision.isPending} onClick={() => void save('stay')}>
              <Save className="h-4 w-4" /> {saveRevision.isPending ? 'กำลังบันทึก' : 'บันทึกรายการ'}
            </button>
            <button className="btn-cta min-h-11" disabled={saveRevision.isPending} onClick={() => void save('continue')}>
              <ArrowRight className="h-4 w-4" /> {saveRevision.isPending ? 'กำลังบันทึก' : 'บันทึกและทำต่อ'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
